import { useState } from "react";
import { CheckCircle2, Phone, Mail, Loader2 } from "lucide-react";
import { Field, Input, Textarea, Select, FileUpload, Consent } from "../components/FormBits";
import { useSeo, Reveal } from "../lib/motion";
import { Breadcrumb } from "../components/CTASection";
import { api, formatError } from "../lib/api";
import { isEmail, isFrenchPhone } from "../lib/validate";
import { useSettings, isSet, telHref, mailHref } from "../context/SettingsContext";
import { toast } from "sonner";

const TYPES = ["Projet climatisation", "Projet gainable", "Froid commercial", "Entretien", "Dépannage", "Autre"];

export default function Contact() {
  useSeo("Contacter HP Froid", "Contactez HP Froid & Climatisation en Alsace pour un projet de climatisation, de froid commercial, un entretien ou un dépannage.");
  const { settings } = useSettings();
  const [f, setF] = useState({ nom: "", prenom: "", telephone: "", email: "", ville: "", type: "", message: "" });
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Merci d’accepter la politique de confidentialité.");
    if (!f.nom || !f.telephone || !f.email) return toast.error("Merci de renseigner nom, téléphone et e-mail.");
    if (!isEmail(f.email)) return toast.error("Veuillez saisir une adresse e-mail valide.");
    if (!isFrenchPhone(f.telephone)) return toast.error("Veuillez saisir un numéro de téléphone français valide.");
    setBusy(true);
    try {
      await api.post("/submissions", { type: "contact", data: { ...f, type_demande: f.type }, files: files.map((x) => x.url) });
      setDone(true);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const infoRows = [
    { icon: Phone, val: settings.phone, href: telHref(settings.phone), val2: settings.phone2, href2: telHref(settings.phone2), label: "Téléphone" },
    { icon: Mail, val: settings.email, href: mailHref(settings.email), label: "E-mail" },
  ].filter((r) => isSet(r.val));

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Contact" }]} />
        <h1 className="font-heading font-black tracking-tight text-4xl md:text-6xl text-ink mt-8">Contacter HP Froid</h1>
        <p className="text-maintext text-lg mt-5 max-w-2xl">Expliquez-nous votre besoin, nous revenons vers vous.</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-28 grid lg:grid-cols-[1.4fr_1fr] gap-14">
        <Reveal>
          {done ? (
            <div data-testid="contact-success" className="bg-cloud border border-line rounded-md p-10 text-center">
              <CheckCircle2 className="mx-auto text-ice" size={48} />
              <h2 className="font-heading font-bold text-2xl text-ink mt-5">Votre demande a bien été transmise à HP Froid.</h2>
              <p className="text-maintext mt-3">Nous revenons vers vous dès que possible.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Nom" required><Input data-testid="contact-nom" value={f.nom} onChange={on("nom")} /></Field>
                <Field label="Prénom"><Input value={f.prenom} onChange={on("prenom")} /></Field>
                <Field label="Téléphone" required><Input type="tel" data-testid="contact-tel" value={f.telephone} onChange={on("telephone")} /></Field>
                <Field label="E-mail" required><Input type="email" data-testid="contact-email" value={f.email} onChange={on("email")} /></Field>
                <Field label="Ville"><Input value={f.ville} onChange={on("ville")} /></Field>
                <Field label="Type de demande"><Select data-testid="contact-type" options={TYPES} value={f.type} onChange={on("type")} /></Field>
              </div>
              <Field label="Votre message"><Textarea value={f.message} onChange={on("message")} /></Field>
              <Field label="Photos (optionnel)"><FileUpload files={files} setFiles={setFiles} testid="contact-files" /></Field>
              <Consent checked={consent} onChange={setConsent} />
              <button type="submit" disabled={busy} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-ice text-white rounded-md px-8 py-4 font-bold hover:bg-icehover transition-transform hover:scale-105 disabled:opacity-60">
                {busy && <Loader2 className="animate-spin" size={18} />} Envoyer ma demande
              </button>
            </form>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-ink text-white rounded-md p-8">
            <h2 className="font-heading font-bold text-xl mb-6">Coordonnées</h2>
            <ul className="space-y-5">
              {infoRows.map((r) => (
                <li key={r.label} className="flex items-start gap-4">
                  <r.icon size={19} className="text-icelight mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-widest text-white/40 font-bold">{r.label}</p>
                    {isSet(r.val) ? (r.href ? <a href={r.href} className="block text-white hover:text-icelight">{r.val}</a> : <p className="text-white">{r.val}</p>) : null}
                    {isSet(r.val2) ? (r.href2 ? <a href={r.href2} className="block text-white hover:text-icelight mt-1">{r.val2}</a> : <p className="text-white mt-1">{r.val2}</p>) : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
